import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { customInstance } from '@/common/infrastructure/http/apiClient';
import { toSieveParams } from '@/common/infrastructure/http/sieve';
import { BLOCKS_LIST_DEFAULT_SORT, buildBlocksSieveFilter } from '@/blocks/application/sieve/buildBlocksSieveFilter';
import { blocksQueryKey, toBlock } from '@/blocks/application/hooks/useBlocks';
import type { Block, BlockKindFilterValue, BlockStatusFilter } from '@/blocks/domain/types';

/** Library gallery page size: each "Load more" fetches the next server page of this size. */
export const BLOCKS_GALLERY_PAGE_SIZE = 12;

export interface UseInfiniteBlocksArgs {
  search?: string;
  kind?: BlockKindFilterValue;
  statusFilter?: BlockStatusFilter;
}

type BlockListItem = Parameters<typeof toBlock>[0];

interface BlocksPage {
  items: BlockListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

function fetchBlocksPage({ search, kind, statusFilter = 'all-active' }: UseInfiniteBlocksArgs, page: number): Promise<BlocksPage> {
  const sieveParams = toSieveParams({
    page,
    pageSize: BLOCKS_GALLERY_PAGE_SIZE,
    filters: buildBlocksSieveFilter({ search, kind, statusFilter }) || undefined,
    sorts: BLOCKS_LIST_DEFAULT_SORT
  });

  return customInstance<BlocksPage>({
    url: '/api/pdf-templates/v1/blocks',
    method: 'GET',
    params: statusFilter === 'all' ? { ...sieveParams, includeArchived: true } : sieveParams
  });
}

/**
 * A row can come back on two pages when the list shifts between the requests (a block created or archived
 * while the user pages): keep its first copy, as `useAllPublishedBlocks` does, so a card is never rendered
 * twice (duplicate React keys, a repeated card in the grid).
 */
function dedupeBlocksById(blocks: Block[]): Block[] {
  const byId = new Map<string, Block>();
  for (const block of blocks) if (!byId.has(block.blockId)) byId.set(block.blockId, block);
  return [...byId.values()];
}

/**
 * The Block Library's list, with real server-side paging: "Load more" requests the NEXT page (`Page=n`,
 * a fixed `PageSize`) and appends it, so the cards already on screen stay mounted (no grid blanking, the
 * scroll position survives) and the list keeps growing past the API's 100-row page cap — with or
 * without Show archived. A new filter keeps the previous result on screen (`keepPreviousData`) until
 * the new one arrives; `isFetching` without `isFetchingNextPage` tells the page that is happening.
 * The API orders rows tied on the sort key by `id`, which keeps the order stable, but pages are not a snapshot:
 * a row CAN repeat when the list shifts between two requests (`dedupeBlocksById` keeps the first copy), and a
 * row archived between two page requests can still be SKIPPED (the dedupe cannot recover a row no page returned).
 */
export const useInfiniteBlocks = (args: UseInfiniteBlocksArgs) => {
  const { search, kind, statusFilter = 'all-active' } = args;
  const query = useInfiniteQuery({
    queryKey: [...blocksQueryKey, 'infinite', search ?? '', kind ?? '', statusFilter] as const,
    queryFn: ({ pageParam }) => fetchBlocksPage({ search, kind, statusFilter }, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.pageNumber + 1 : undefined),
    placeholderData: keepPreviousData,
    // The library always shows fresh data when it mounts (a block archived in the editor must not linger).
    staleTime: 0,
    refetchOnMount: 'always'
  });

  const pages = query.data?.pages ?? [];
  const items = dedupeBlocksById(pages.flatMap((page) => page.items).map(toBlock));

  return {
    // The newest page carries the freshest total, so "Showing n of N" follows rows created or removed while paging.
    data: query.data ? { items, totalCount: pages.at(-1)?.totalCount ?? items.length } : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    // True when only a "Load more" failed: the earlier pages are still in `data`, so the page keeps its grid.
    isFetchNextPageError: query.isFetchNextPageError,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    refetch: () => void query.refetch()
  };
};
